// v2
// Path: frontend/src/App.jsx
// Inputs: None (root application component)
// Outputs: Authenticated Dashboard or Login screen with session state
// Summary: Manages authentication lifecycle, pro-actively verifies JWT expiry, enforces auto-logout timers and interceptors on 401/403 status codes.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';

function parseJwt(token) {
    if (!token || typeof token !== 'string') return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
}

function isTokenExpired(token) {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    // Expire 5 seconds ahead of exact timestamp to avoid edge-of-expiry race conditions
    return Date.now() >= (payload.exp * 1000) - 5000;
}

function getTokenExpiry(token) {
    const payload = parseJwt(token);
    return payload && payload.exp ? payload.exp * 1000 : null;
}

export default function App() {
    const [sessionNotice, setSessionNotice] = useState(() => {
        const storedToken = localStorage.getItem('sapphire_token');
        if (storedToken && isTokenExpired(storedToken)) {
            localStorage.removeItem('sapphire_token');
            localStorage.removeItem('sapphire_user');
            return 'Your session has expired. Please log in again.';
        }
        return null;
    });

    const [token, setToken] = useState(() => {
        const stored = localStorage.getItem('sapphire_token');
        if (stored && !isTokenExpired(stored)) {
            return stored;
        }
        return null;
    });

    const [user, setUser] = useState(() => {
        const storedToken = localStorage.getItem('sapphire_token');
        if (!storedToken || isTokenExpired(storedToken)) return null;
        const storedUser = localStorage.getItem('sapphire_user');
        if (storedUser) {
            try {
                return JSON.parse(storedUser);
            } catch {
                return null;
            }
        }
        return null;
    });

    const sessionExpiredRef = useRef(false);

    const handleLogout = useCallback((reason = null) => {
        localStorage.removeItem('sapphire_token');
        localStorage.removeItem('sapphire_user');
        setToken(null);
        setUser(null);
        sessionExpiredRef.current = false;
        if (reason) {
            setSessionNotice(reason);
        }
    }, []);

    const handleLogin = useCallback((data) => {
        sessionExpiredRef.current = false;
        setSessionNotice(null);
        localStorage.setItem('sapphire_token', data.token);
        localStorage.setItem('sapphire_user', JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
    }, []);

    // Proactive token expiration timer & tab visibility checker
    useEffect(() => {
        if (!token) return undefined;

        if (isTokenExpired(token)) {
            handleLogout('Your session has expired. Please log in again.');
            return undefined;
        }

        const expiryTime = getTokenExpiry(token);
        let timer = null;
        if (expiryTime) {
            const timeUntilExpiry = expiryTime - Date.now();
            if (timeUntilExpiry > 0) {
                timer = setTimeout(() => {
                    console.warn('Session expired by timeout. Logging out.');
                    handleLogout('Your session has expired. Please log in again.');
                }, timeUntilExpiry);
            } else {
                handleLogout('Your session has expired. Please log in again.');
                return undefined;
            }
        }

        const checkExpiry = () => {
            if (isTokenExpired(token)) {
                console.warn('Session expired on tab visibility change. Logging out.');
                handleLogout('Your session has expired. Please log in again.');
            }
        };

        window.addEventListener('visibilitychange', checkExpiry);
        window.addEventListener('focus', checkExpiry);

        return () => {
            if (timer) clearTimeout(timer);
            window.removeEventListener('visibilitychange', checkExpiry);
            window.removeEventListener('focus', checkExpiry);
        };
    }, [token, handleLogout]);

    // Global fetch interceptor to catch 401s and 403s and force immediate logout
    useEffect(() => {
        const originalFetch = window.fetch;
        window.fetch = async function(...args) {
            const response = await originalFetch.apply(this, args);
            const url = args[0] ? (typeof args[0] === 'string' ? args[0] : args[0].url || '') : '';

            // Ignore credentials rejection on the login endpoint itself
            if ((response.status === 401 || response.status === 403) && !url.includes('/api/login')) {
                if (!sessionExpiredRef.current) {
                    sessionExpiredRef.current = true;
                    console.warn(`Session expired or unauthorized (${response.status}) on ${url}. Forcing logout.`);
                    handleLogout('Your session has expired. Please log in again.');
                }
            }
            return response;
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, [handleLogout]);

    if (!token) return <Login onLogin={handleLogin} notice={sessionNotice} />;
    return <Dashboard user={user} token={token} onLogout={handleLogout} />;
}